import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import type { DateRangeValue } from '@ui';
import { FilterLabelRow, OngoingCloseBadge } from '@ui';
import { getDateLabel, isOngoingPeriod } from './date_range_label';
import {
  useCompanyUsersQuery,
  type CompanyUserSortField,
  type SortDirection,
} from '@hooks/requests/queries/use_company_users_query';

// ── Types ──────────────────────────────────────────────────────────────────────

export type CompanyUserListProps = {
  companyId: string;
  /** AceUp staff only — org the company belongs to. Ignored for Partner roles (JWT-scoped). */
  organizationId?: string | null;
  dateRange: DateRangeValue | null;
};

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

// ── Formatting helpers ───────────────────────────────────────────────────────────

const toDateStr = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const formatNumber = (n: number): string => n.toLocaleString();

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateShort = (iso: string | null): string => {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function pageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '...')[] = [];
  if (current <= 3) {
    pages.push(1, 2, 3, 4, '...', total);
  } else if (current >= total - 2) {
    pages.push(1, '...', total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '...', current - 1, current, current + 1, '...', total);
  }
  return pages;
}

// ── Component ──────────────────────────────────────────────────────────────────

const CompanyUserList: React.FC<CompanyUserListProps> = ({ companyId, organizationId, dateRange }) => {
  const [sortField, setSortField] = useState<CompanyUserSortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reset back to page 1 whenever the company or sort changes — the
  // previous page number may no longer exist for a smaller result set.
  useEffect(() => {
    setPage(1);
  }, [companyId, sortField, sortDir]);

  const from = dateRange ? toDateStr(dateRange.from) : undefined;
  const to = dateRange ? toDateStr(dateRange.to) : undefined;

  const { data, isLoading, isError } = useCompanyUsersQuery({
    companyId,
    from,
    to,
    page,
    limit: pageSize,
    sort: sortField,
    dir: sortDir,
    organizationId,
    enabled: Boolean(companyId),
  });

  const dateLabel = getDateLabel(dateRange);
  const ongoing = isOngoingPeriod(dateRange);

  const users = data?.users ?? [];
  const totalItems = data?.totalItems ?? 0;
  const totalUsers = data?.totalUsers ?? 0;
  const totalPages = data?.totalPages === 0 ? 1 : data?.totalPages;

  const handleHeaderSort = (field: CompanyUserSortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const handlePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const Th = ({ field, label }: { field: CompanyUserSortField; label: string }) => {
    const active = sortField === field;
    return (
      <th className="cul-th">
        <button
          type="button"
          className={`cul-th-btn${active ? ' cul-th-btn--active' : ''}`}
          onClick={() => handleHeaderSort(field)}
        >
          <span className="cul-th-text">{label}</span>
          {active ? (
            <FontAwesomeIcon
              icon={faArrowDown}
              className={`cul-sort-arrow${sortDir === 'asc' ? ' cul-sort-arrow--asc' : ''}`}
            />
          ) : (
            <span className="cul-sort-inactive">↕</span>
          )}
        </button>
      </th>
    );
  };

  return (
    <div className="cul-card">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="cul-header">
        <p className="cul-eyebrow">User list</p>
        {/* Shared card context row (period + Ongoing/Closed), same as the
            other dashboard cards. No org/company segment — the page header
            already names both. */}
        <div className="suc-filter-row">
          <FilterLabelRow companyLabel="" dateLabel={dateLabel} />
          <OngoingCloseBadge isOngoing={ongoing} />
        </div>
      </div>

      {/* ── Table settings bar ─────────────────────────────────── */}
      <div className="cul-settings-bar">
        <p className="cul-count">
          <strong>{formatNumber(totalUsers)}</strong> users
        </p>

        <div className="cul-settings-right">
          <span className="cul-show-label">Show:</span>
          <div className="cul-page-size-btn">
            <div className="cul-page-size-left">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="cul-tablerows-icon">
                <rect x="1" y="2" width="10" height="2" rx="0.5" fill="currentColor" />
                <rect x="1" y="5" width="10" height="2" rx="0.5" fill="currentColor" />
                <rect x="1" y="8" width="10" height="2" rx="0.5" fill="currentColor" />
              </svg>
              <select
                value={pageSize}
                onChange={(e) => handlePageSize(Number(e.target.value))}
                className="cul-page-size-select"
                aria-label="Rows per page"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={`page-size-${n}`} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="cul-page-size-chevron" aria-hidden>
              <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <div className="cul-pagination">
            <button
              type="button"
              className="cul-pg-arrow"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              aria-label="Previous page"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M6 2L3.5 5 6 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {pageNumbers(page, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="cul-pg-ellipsis">…</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  className={`cul-pg-num${page === p ? ' cul-pg-num--active' : ''}`}
                  onClick={() => setPage(p as number)}
                  aria-current={page === p ? 'page' : undefined}
                >
                  {p}
                </button>
              )
            )}

            <button
              type="button"
              className="cul-pg-arrow"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Next page"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M4 2L6.5 5 4 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────── */}
      <div className="cul-table-wrap">
        <table className="cul-table">
          <colgroup>
            <col className="cul-col-user" />
            <col className="cul-col-messages" />
            <col className="cul-col-lastactive" />
            <col className="cul-col-createdon" />
          </colgroup>
          <thead>
            <tr>
              <th className="cul-th cul-th--first">
                <span className="cul-th-text">User</span>
              </th>
              <Th field="totalMessages" label="Total messages" />
              <Th field="lastActive" label="Last active" />
              <Th field="createdAt" label="Created on" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className="cul-empty-cell">Loading…</td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={4} className="cul-empty-cell">Failed to load users</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={4} className="cul-empty-cell">No users to display</td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.userId} className="cul-row">
                  <td className="cul-td cul-td--first">{user.firstName}</td>
                  <td className="cul-td">{formatNumber(user.totalMessages)}</td>
                  <td className="cul-td cul-td--muted">{formatDateShort(user.lastActive)}</td>
                  <td className="cul-td cul-td--muted">{formatDate(user.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyUserList;
